// ============================================================
// HELIX (DnaHelix3D) — FROZEN 2026-05-20 by Bryan K. Short
// Do not modify visually until a planned redesign happens.
// Mechanical fixes (data binding, perf, prop changes) ARE allowed.
// Visual changes (geometry, palette, dimensions, motion) are NOT.
// ============================================================

/**
 * DnaHelix3D.tsx — Direct Three.js Helix Mount (no iframe)
 *
 * Mounts the helix.js Three.js scene directly into a React div — no iframe,
 * no postMessage. Full two-way sync via direct JS calls and callbacks.
 *
 * Visual: identical to mi6group.com marketing helix (same helix.js source)
 *
 * Props:
 *   cpScores     — { cp1: 75, cp2: 40, ... } map of CP key → 0-100 pct score
 *   selectedCp   — currently selected CP number (1-10) or null
 *   onSelectCp   — user clicked an orb or the helix initiated a selection
 *   composite    — 0-100 composite score (overlaid on canvas)
 *   autoTour     — when true, helix follows user interaction (auto-spin to CP)
 *   accountId    — used as a cache-bust key for re-mount on account switch
 */

import { useEffect, useRef, useCallback, useState } from "react";
import TowerOverlay, { CpState } from "./TowerOverlay";
import { getHealthColor } from "@shared/schema";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SceneObject {
  userData?: Record<string, unknown>;
  visible: boolean;
  traverse: (fn: (obj: SceneObject) => void) => void;
}

interface HelixInternal {
  scene: SceneObject;
  camera: {
    position: { x: number; y: number; z: number; set: (x: number, y: number, z: number) => void };
    aspect?: number;
    updateProjectionMatrix?: () => void;
    lookAt: (x: number, y: number, z: number) => void;
  };
  renderer: { setSize: (w: number, h: number, updateStyle?: boolean) => void };
  controls: {
    enabled: boolean;
    autoRotate: boolean;
    autoRotateSpeed?: number;
    target: { set: (x: number, y: number, z: number) => void };
    addEventListener: (evt: string, fn: () => void) => void;
  };
  cpRings: Array<{
    cp: { cp: number; name: string; hardKill?: boolean };
    ci: number;
    poleOrb: {
      material: {
        color: { setHex: (n: number) => void };
        emissive: { setHex: (n: number) => void };
        emissiveIntensity: number;
      };
      scale: { set: (x: number, y: number, z: number) => void };
    };
  }>;
}

interface OrbScreenPos {
  ci: number;
  cp: number;
  x: number;
  y: number;
  visible: boolean;
  z: number;
}

interface HelixInstance {
  destroy: () => void;
  setCamera: (preset: string) => void;
  selectCP: (ci: number) => void;
  goToCp?: (ci: number, opts?: { dir?: number }) => void;
  goToQuestion?: (qSeq: number) => void;
  setIdle?: () => void;
  deselect?: () => void;
  getCpScreenPositions?: () => OrbScreenPos[];
  _internal: HelixInternal;
}

type InitHelix = (
  el: HTMLElement,
  opts: Record<string, unknown>,
) => Promise<HelixInstance | null>;

// ─── Helix color mapping — CANON.md §5 (5-band, locked 2026-05-23) ─────────────
// Three.js wants integer literals; convert canon hex by stripping the leading #.
// Green 65+ · Yellow 50-64 · Orange 30-49 · Red <30 (canon §6, single hex per band)

function scoreToHex(pct: number): number {
  if (pct >= 80) return 0x16A34A; // green  (Commit)
  if (pct >= 65) return 0x16A34A; // green  (Upside)
  if (pct >= 50) return 0xEAB308; // yellow (Best Case)
  if (pct >= 30) return 0xEA580C; // orange (Pipeline)
  return 0xDC2626;                // red    (Omit)
}

// Evidence coverage types
export type EvidenceCoverage = 'evidence' | 'assumption' | 'mixed' | 'open';

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  cpScores: Record<string, number>; // { cp1: 75, cp2: 40, ... }
  selectedCp: number | null;
  onSelectCp: (cp: number | null) => void;
  composite: number;
  autoTour?: boolean;
  accountId?: string;
  /** Evidence coverage per CP: 'evidence' | 'assumption' | 'mixed' | 'open' */
  evidenceCoverage?: Record<string, EvidenceCoverage>;
  /** Show tower exterior overlay (Option B implied rails) */
  showTowerOverlay?: boolean;
  /** When true, suppress the per-orb score pills overlaid on the helix.
   *  Use in readOnly/view-only contexts where the pill layer is redundant. */
  hidePills?: boolean;
  /**
   * W61: controls CP ordering along the vertical axis.
   * 'top-down' (default, Galaxy / legacy): CP1 at top, CP10 at bottom.
   * 'bottom-up' (Tower canon, /opportunity-dna): CP1 at waterline, CP10 at roof.
   */
  helixOrientation?: "bottom-up" | "top-down";
}

export default function DnaHelix3D({
  cpScores,
  selectedCp,
  onSelectCp,
  composite,
  autoTour = true,
  accountId,
  evidenceCoverage = {},
  showTowerOverlay = false, // W3-Tower: tower and helix are SEPARATE visuals
  hidePills = false,
  helixOrientation = "top-down",
}: Props) {
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  const mountRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const helixRef = useRef<HelixInstance | null>(null);
  const loadedRef = useRef(false);
  const prevSelectedCp = useRef<number | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userDragging = useRef(false);
  const autoTourPaused = useRef(false);
  const [orbPositions, setOrbPositions] = useState<OrbScreenPos[]>([]);
  // v2: alternating-direction state — flips every CP transition.
  const lastSpinDir = useRef<1 | -1>(1);
  // W61: store orientation in a ref so the boot callback always reads the latest value
  // even though boot uses an empty dependency array.
  const helixOrientationRef = useRef(helixOrientation);
  helixOrientationRef.current = helixOrientation;

  // ── Track container size for TowerOverlay ────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) {
        setContainerSize({ width: e.contentRect.width, height: e.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Load the helix.js module once ──────────────────────────────────────────
  const boot = useCallback(async () => {
    const el = mountRef.current;
    if (!el || loadedRef.current) return;
    loadedRef.current = true;

    // Inject CSS once
    if (!document.querySelector('link[href*="helix.css"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/helix/helix.css";
      document.head.appendChild(link);
    }

    // Load helix.js module once — store initHelix on window.
    //
    // ROOT CAUSE OF "missing helix" REGRESSION (Bryan, 2026-05-07):
    //   Inline <script type="module"> tags NEVER fire the 'load' event,
    //   so the previous code always fell through to a 5-second setTimeout
    //   fallback before calling initHelix(). Users navigating to the DNA
    //   page saw an empty middle column for 5+ seconds and assumed the
    //   helix was missing entirely.
    //
    // FIX: Have the inline module dispatch a custom event (and set a flag)
    //   when it finishes evaluating. Resolve the await as soon as the event
    //   fires — typically ~200–400ms. The setTimeout(8000) is now a hard
    //   safety guard, not the normal path.
    const win = window as unknown as Record<string, unknown>;
    if (!win.__initHelixAip) {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        // Listen for the ready event the module will dispatch.
        window.addEventListener("aip:helix-module-ready", finish, { once: true });

        // Poll as a belt-and-braces guard — covers the case where the event
        // is missed (e.g. CSP / browser quirks) but the module did set the flag.
        const start = Date.now();
        const poll = setInterval(() => {
          if (win.__initHelixAip) {
            clearInterval(poll);
            finish();
          } else if (Date.now() - start > 8000) {
            clearInterval(poll);
            finish();
          }
        }, 50);

        const s = document.createElement("script");
        s.type = "module";
        s.textContent = [
          "import { initHelix } from '/helix/helix.js?v=w61-bottom-up';",
          "window.__initHelixAip = initHelix;",
          "window.dispatchEvent(new Event('aip:helix-module-ready'));",
        ].join("\n");
        document.head.appendChild(s);
      });
    }

    const initHelix = win.__initHelixAip as InitHelix | undefined;
    if (!initHelix || !mountRef.current) return;

    const helix = await initHelix(mountRef.current, {
      autoRotate: false,
      height: "100%",
      showCameraControls: false,
      disablePaywall: true,
      dataUrl: "/helix/helix-data.json",
      // W61: pass orientation so /opportunity-dna renders CP1 at bottom (Tower canon).
      orientation: helixOrientationRef.current,
    });

    if (!helix) return;
    helixRef.current = helix;

    // Hide built-in chrome (we render composite + controls in React)
    const hide = (sel: string) => {
      el.querySelectorAll<HTMLElement>(sel).forEach((n) => {
        n.style.display = "none";
      });
    };
    hide(".mi6-helix__hint");
    hide(".mi6-helix__brand");
    hide(".mi6-helix__cam-controls");
    hide(".mi6-helix__panel");
    hide(".mi6-helix__modal-backdrop");
    hide(".mi6-helix__tooltip");

    // Track manual drag to pause auto-tour for the session
    helix._internal.controls.addEventListener("start", () => {
      userDragging.current = true;
      autoTourPaused.current = true;
    });

    // Apply initial CP colors from scores
    applyScoreColors(helix, cpScores, null);

    // Intercept orb clicks — the helix's click handler emits cp-selected messages;
    // we also listen to the helix's internal selectCP callback via overriding it.
    // Since helix.js uses postMessage to communicate selections, listen here:
    const msgHandler = (e: MessageEvent) => {
      if (!e.data || e.data.source !== "mi6-helix") return;
      if (e.data.type === "cp-selected") {
        const cpNum = e.data.cpNumber as number;
        onSelectCp(cpNum);
        scheduleIdleReturn();
        // v2: cross-component notification so QaPanel resets question seq.
        window.dispatchEvent(new CustomEvent("aip:cp-active", { detail: { cp: cpNum } }));
      } else if (e.data.type === "cp-deselected") {
        onSelectCp(null);
        window.dispatchEvent(new CustomEvent("aip:cp-active", { detail: { cp: null } }));
      }
    };
    window.addEventListener("message", msgHandler);

    // v2: Question-level micro-rotation. QaPanel emits aip:question-active
    // with detail.seq (monotonic counter). Sign alternates inside helix.js.
    const questionHandler = (e: Event) => {
      const ev = e as CustomEvent<{ seq: number }>;
      const seq = ev.detail?.seq ?? 0;
      helixRef.current?.goToQuestion?.(seq);
    };
    window.addEventListener("aip:question-active", questionHandler as EventListener);

    // Store cleanup ref
    (mountRef.current as HTMLDivElement & { __helixCleanup?: () => void }).__helixCleanup = () => {
      window.removeEventListener("message", msgHandler);
      window.removeEventListener("aip:question-active", questionHandler as EventListener);
    };

    // Idle return: after 3s with no interaction, return to home
    scheduleIdleReturn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    boot();
    return () => {
      // Cleanup on unmount
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      const el = mountRef.current as (HTMLDivElement & { __helixCleanup?: () => void }) | null;
      el?.__helixCleanup?.();
      if (helixRef.current) {
        helixRef.current.destroy();
        helixRef.current = null;
        loadedRef.current = false;
      }
    };
  }, [boot]);

  // Re-mount on account switch
  useEffect(() => {
    if (!accountId) return;
    if (helixRef.current) {
      helixRef.current.destroy();
      helixRef.current = null;
    }
    loadedRef.current = false;
    autoTourPaused.current = false;
    userDragging.current = false;
    void boot();
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep CP colors in sync with scores ──────────────────────────────────────
  useEffect(() => {
    if (!helixRef.current) return;
    // Skip active CP so the red highlight is not overwritten by score color
    const activeCi = selectedCp != null ? selectedCp - 1 : null;
    applyScoreColors(helixRef.current, cpScores, activeCi);
  }, [cpScores, selectedCp]);

  // ── Apply evidence coverage to orb visuals ────────────────────────────────
  useEffect(() => {
    if (!helixRef.current) return;
    applyEvidenceCoverage(helixRef.current, evidenceCoverage);
  }, [evidenceCoverage]);

  // ── Project orb world positions to screen coords each frame ─────────────────
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const fn = helixRef.current?.getCpScreenPositions;
      if (fn) {
        try {
          const pos = fn();
          setOrbPositions(pos);
        } catch {
          /* ignore */
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Helix orients to selectedCp when changed externally (from legend/panel) ─
  useEffect(() => {
    if (!helixRef.current) return;
    if (selectedCp === prevSelectedCp.current) return;
    prevSelectedCp.current = selectedCp;

    if (selectedCp === null) {
      // Return to home
      if (helixRef.current.deselect) helixRef.current.deselect();
      else returnToHome(helixRef.current);
    } else {
      // Fly to CP (ci = index 0-based = cpNumber - 1) with alternating spin direction.
      const ci = selectedCp - 1;
      if (ci >= 0 && ci < helixRef.current._internal.cpRings.length) {
        const dir = lastSpinDir.current;
        if (helixRef.current.goToCp) {
          helixRef.current.goToCp(ci, { dir });
        } else {
          helixRef.current.selectCP(ci);
        }
        // Flip direction for next call (bidirectional spin per spec)
        lastSpinDir.current = (dir === 1 ? -1 : 1) as 1 | -1;
        scheduleIdleReturn();
      }
    }
  }, [selectedCp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Idle return scheduling ─────────────────────────────────────────────────
  function scheduleIdleReturn() {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (!autoTourPaused.current && helixRef.current) {
        returnToHome(helixRef.current);
      }
    }, 3000);
  }

  // ── Home position restore ──────────────────────────────────────────────────
  function returnToHome(helix: HelixInstance) {
    // v2: restore idle continuous rotation in last direction.
    helix._internal.controls.target.set(0, 0, 0);
    helix.setIdle?.();
  }

  // Score color — five-color doctrine (CANON.md §5 palette via getHealthColor)
  // Hotfix 2026-05-27 — ORB_CSS5/classifyOrb5 were never defined; this caused a
  // ReferenceError on every render and broke Helix Demo + Galaxy. Replaced with
  // the canonical getHealthColor() from shared/schema.
  const compositeColor = getHealthColor(composite);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Three.js canvas mount */}
      <div
        ref={mountRef}
        style={{ width: "100%", height: "100%", minHeight: 0 }}
        aria-label="DNA of the Deal — 3D helix"
      />

      {/* Tower exterior overlay (Option B — implied rails) */}
      {showTowerOverlay && containerSize.width > 0 && (
        <TowerOverlay
          width={containerSize.width}
          height={containerSize.height}
          showFloorLabels={true}
          currentCp={
            selectedCp != null && selectedCp >= 1 && selectedCp <= 10
              ? selectedCp : null
          }
          cpState={(() => {
            if (selectedCp == null) return null;
            const s = cpScores[`cp${selectedCp}`] ?? null;
            if (s == null || isNaN(s)) return "not-started" as CpState;
            if (s >= 80) return "passed" as CpState;
            if (s >= 60) return "in-progress" as CpState;
            if (s >= 40) return "at-risk" as CpState;
            if (s >  0)  return "failed" as CpState;
            return "not-started" as CpState;
          })()}
        />
      )}

      {/* Per-orb score pills — projected from world coords.
          hidePills=true suppresses these in readOnly/view-only contexts. */}
      {!hidePills && orbPositions.map((p) => {
        if (!p.visible) return null;
        const pct = cpScores[`cp${p.cp}`] ?? 0;
        const c = getHealthColor(pct);
        return (
          <div
            key={`orb-pill-${p.ci}`}
            style={{
              position: "absolute",
              left: p.x,
              top: p.y - 26,
              transform: "translate(-50%, -100%)",
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 7px",
              borderRadius: 999,
              background: "rgba(5,9,18,0.9)",
              border: `1px solid ${c}`,
              color: c,
              fontSize: 10,
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
              pointerEvents: "none",
              zIndex: 9,
              whiteSpace: "nowrap",
              textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            }}
          >
            <span
              title={`CP${p.cp}`}
              style={{ color: "#8E8E94", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180, display: "inline-block" }}
            >CP{p.cp}</span>
            <span>{pct}</span>
          </div>
        );
      })}

      {/* Composite score overlay — always visible */}
      <div
        style={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(5,9,18,0.82)",
          border: `1px solid ${compositeColor}55`,
          borderRadius: 28,
          padding: "6px 16px 6px 10px",
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
          zIndex: 10,
        }}
      >
        {/* Ring */}
        <svg width={36} height={36} viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
          <circle cx={18} cy={18} r={14} fill="none" stroke="rgba(107,114,128,0.18)" strokeWidth={4} />
          <circle
            cx={18}
            cy={18}
            r={14}
            fill="none"
            stroke={compositeColor}
            strokeWidth={4}
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 14}`}
            strokeDashoffset={`${2 * Math.PI * 14 * (1 - composite / 100)}`}
            style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1), stroke 0.4s ease" }}
          />
        </svg>
        <div>
          <div style={{ fontSize: 9, fontWeight: 600, color: "#6B6B70", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Composite score
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: compositeColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {composite}
            <span style={{ fontSize: 10, fontWeight: 500, color: "#6B6B70" }}>/100</span>
          </div>
        </div>
      </div>

      {/* Hint */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 10,
          color: "rgba(107,114,128,0.5)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
          zIndex: 10,
        }}
      >
        Tap orbs to navigate · Drag to rotate · Scroll to zoom
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Apply evidence coverage type to orb materials
// 'evidence' = solid filled (score color), 'assumption' = amber outline,
// 'mixed' = solid + note for ring, 'open' = keep default
function applyEvidenceCoverage(
  helix: HelixInstance,
  evidenceCoverage: Record<string, import("./DnaHelix3D").EvidenceCoverage>
) {
  const rings = helix._internal?.cpRings;
  if (!rings) return;
  rings.forEach((ring, ci) => {
    const cpKey = `cp${ci + 1}`;
    const coverage = evidenceCoverage[cpKey];
    if (!coverage || coverage === 'open') return;
    try {
      if (coverage === 'assumption') {
        // Amber dashed ring signal — set emissive to amber
        ring.poleOrb.material.color.setHex(0xd97706);
        ring.poleOrb.material.emissive.setHex(0xd97706);
        ring.poleOrb.material.emissiveIntensity = 0.9;
      }
      // For 'evidence' and 'mixed', colors are already set by scoreToHex
      // The React overlay handles the dashed amber ring animation
    } catch {
      // material not accessible yet
    }
  });
}

function applyScoreColors(helix: HelixInstance, cpScores: Record<string, number>, activeCi?: number | null) {
  const rings = helix._internal.cpRings;
  if (!rings) return;
  rings.forEach((ring, ci) => {
    // Do NOT override the active CP's red highlight
    if (activeCi != null && ci === activeCi) return;
    const cpNum = ci + 1;
    const pct = cpScores[`cp${cpNum}`] ?? 0;
    const hex = scoreToHex(pct);
    try {
      ring.poleOrb.material.color.setHex(hex);
      ring.poleOrb.material.emissive.setHex(hex);
    } catch {
      // material may not be accessible yet
    }
  });
}
